import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Image, Modal, Spin, message } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/serverApiConfig";
import { publicSrc, thumbSrc } from "../../utils/imageVariant";
import { warmAround, warmList, warmSrc, whenIdle } from "../../utils/imageWarm";
import "./styles.css";

const albumCache = new Map();

function albumKey(patientId, bar) {
  return `${patientId}:${bar}`;
}

export function prefetchBeforeAfter(patientId, bar = "before") {
  if (!patientId) return Promise.resolve(null);
  const key = albumKey(patientId, bar);
  if (albumCache.has(key)) return albumCache.get(key);
  const request = axios
    .get(`${API_BASE_URL}patient/comparsion/${patientId}`, { params: { bar } })
    .then((res) => res.data)
    .catch((error) => {
      albumCache.delete(key);
      throw error;
    });
  albumCache.set(key, request);
  return request;
}

function useInView(eager, margin = "160px 0px") {
  const ref = useRef(null);
  const [visible, setVisible] = useState(!!eager);

  useEffect(() => {
    if (visible) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: margin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, margin]);

  return [ref, visible];
}

function preload(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
}

function BaThumb({ url, alt, eager, onDelete }) {
  const [tileRef, visible] = useInView(eager);
  const low = thumbSrc(url);
  const mid = publicSrc(url);
  const [src, setSrc] = useState(low);
  const [ready, setReady] = useState(false);
  const sharp = src === mid;

  useEffect(() => {
    setSrc(low);
    setReady(false);
  }, [low]);

  useEffect(() => {
    if (!visible || src === mid) return undefined;
    let cancelled = false;
    preload(mid)
      .then((next) => {
        if (!cancelled) setSrc(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mid, src, visible]);

  return (
    <div ref={tileRef} className={`ba-tile${ready ? " is-on" : ""}${sharp ? " is-sharp" : " is-soft"}`}>
      {!ready && <div className="ba-skel" />}
      <PhotoView src={mid}>
        <div className="ba-tile-photo">
          <Image
            alt={alt}
            src={src}
            preview={false}
            width="100%"
            height="100%"
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "low"}
            onLoad={() => setReady(true)}
            onError={() => {
              if (src === low) setSrc(mid);
            }}
          />
        </div>
      </PhotoView>
      <button
        type="button"
        className="ba-tile-del"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
      >
        <DeleteOutlined />
      </button>
    </div>
  );
}

function applyAlbum(payload, bar, previous) {
  const before = payload.before || [];
  const after = payload.after || [];
  const splitApi = payload.beforeCount != null || payload.afterCount != null;
  return {
    before: bar === "before" || !splitApi || before.length ? before : previous.before,
    after: bar === "after" || !splitApi || after.length ? after : previous.after,
  };
}

function Album({ title, side, items, srcKey, count, uploading, onAdd, onDelete }) {
  return (
    <section className="ba-album">
      <div className="ba-album-head">
        <h3>
          {title} <span>{count}</span>
        </h3>
        <button type="button" className="ba-add-btn" onClick={() => onAdd(side)} disabled={uploading === side}>
          {uploading === side ? <Spin size="small" /> : <PlusOutlined />}
          <span>زیادکردن</span>
        </button>
      </div>

      {items.length === 0 ? (
        <div className="ba-empty">هیچ وێنەیەک نییە.</div>
      ) : (
        <div className="ba-grid">
          {items.map((item, index) => (
            <BaThumb
              key={item.id}
              alt={title}
              eager={index < 2}
              url={item[srcKey]}
              onDelete={() => {
                Modal.confirm({
                  title: "ئایا دڵنیایت لە سڕینەوەی وێنە؟",
                  okText: "بەڵێ",
                  cancelText: "نەخێر",
                  onOk: () => onDelete(side, item),
                });
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function albumUrls(data) {
  return [
    ...data.before.map((item) => publicSrc(item.befor)),
    ...data.after.map((item) => publicSrc(item.after)),
  ].filter(Boolean);
}

function BeforeAfter({ onTabCount }) {
  const { id } = useParams();
  const inputRef = useRef(null);
  const pendingSide = useRef("before");
  const [uploading, setUploading] = useState("");
  const [data, setData] = useState({ before: [], after: [] });
  const [counts, setCounts] = useState({ before: 0, after: 0 });
  const slideUrls = useMemo(() => albumUrls(data), [data]);

  const prepareSlide = useCallback(
    (index) => {
      warmAround(slideUrls, index);
      const current = slideUrls[index];
      if (current) warmSrc(current, 0);
    },
    [slideUrls]
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([prefetchBeforeAfter(id, "before"), prefetchBeforeAfter(id, "after")]).then(([before, after]) => {
      if (cancelled) return;
      const payload = {
        before: before?.before || [],
        after: after?.after || [],
        beforeCount: before?.beforeCount ?? (before?.before || []).length,
        afterCount: after?.afterCount ?? (after?.after || []).length,
      };
      setCounts({ before: payload.beforeCount, after: payload.afterCount });
      setData((previous) => applyAlbum(payload, null, previous));
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!slideUrls.length) return undefined;
    return whenIdle(() => warmList(slideUrls, 10));
  }, [slideUrls]);

  const pickFiles = (bar) => {
    pendingSide.current = bar;
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const onFilesChosen = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const side = pendingSide.current;
    const formData = new FormData();
    files.forEach((file) => formData.append("image", file));
    setUploading(side);
    axios
      .post(`${API_BASE_URL}patient/comparsion?p_id=${id}&bar=${side}`, formData)
      .then((res) => {
        albumCache.delete(albumKey(id, side));
        setData((previous) => ({ ...previous, [side]: previous[side].concat(res.data[side] || []) }));
        const added = (res.data[side] || []).length;
        setCounts((previous) => ({ ...previous, [side]: previous[side] + added }));
        onTabCount?.(side, added);
        message.success("وێنەکە زیادکرا");
      })
      .catch(() => {
        message.error("هەڵەیەک ڕویدا لە زیادکردنی وێنە!");
      })
      .finally(() => {
        setUploading("");
      });
  };

  const handleDelete = useCallback((side, rec) => {
    return axios
      .delete(`${API_BASE_URL}patient/comparsion/${rec.id}?bar=${side}`)
      .then(() => {
        albumCache.delete(albumKey(id, side));
        setData((previous) => ({ ...previous, [side]: previous[side].filter((item) => item.id !== rec.id) }));
        setCounts((previous) => ({ ...previous, [side]: Math.max(0, previous[side] - 1) }));
        onTabCount?.(side, -1);
      })
      .catch(() => {
        message.error("هەڵەیەک هەیە لە سڕینەوەی وێنە");
      });
  }, [id, onTabCount]);

  return (
    <div className="ba-page">
      <input ref={inputRef} className="ba-file" type="file" accept="image/*" multiple onChange={onFilesChosen} />

      <PhotoProvider
        maskOpacity={0.88}
        bannerVisible
        speed={() => 220}
        onVisibleChange={(visible, index) => {
          if (visible) prepareSlide(index);
        }}
        onIndexChange={(index) => prepareSlide(index)}
      >
        <div className="ba-albums">
          <Album
            title="پێشتر"
            side="before"
            srcKey="befor"
            items={data.before}
            count={counts.before || data.before.length}
            uploading={uploading}
            onAdd={pickFiles}
            onDelete={handleDelete}
          />
          <Album
            title="دواتر"
            side="after"
            srcKey="after"
            items={data.after}
            count={counts.after || data.after.length}
            uploading={uploading}
            onAdd={pickFiles}
            onDelete={handleDelete}
          />
        </div>
      </PhotoProvider>
    </div>
  );
}

export default BeforeAfter;
